import { createClient as createSupabaseClient } from '@supabase/supabase-js'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Browser-side Supabase client — SINGLETON.
 * All callers (AuthSessionProvider, apiFetch, RealtimeProvider) share
 * the same instance so they share the same in-memory session cache.
 * Without this, hard-refresh causes race conditions where one caller
 * has the session and another doesn't.
 */
let _client: SupabaseClient | null = null

// DEV-ONLY (05.06.2026): localhost-Erkennung für den No-op-Lock unten.
// import.meta.env.DEV wird in Prod zu `false` ge-tree-shaked → in Prod nie aktiv.
function isLocalhostDev(): boolean {
  if (!import.meta.env.DEV) return false
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '::1'
}

export function createClient(): SupabaseClient {
  if (_client) return _client
  const url = import.meta.env.VITE_SUPABASE_URL || ''
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
  if (!url || !key) {
    throw new Error(
      'Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local',
    )
  }
  _client = createSupabaseClient(url, key, {
    auth: {
      // 28.04.2026 Master-Readiness P0-3: Fix portiert aus admin-console (f3383b7
      // Login-Saga 26.04). Default detectSessionInUrl=true startet einen
      // impliziten exchangeCodeForSession sobald `?code=` in URL ist.
      // Parallel macht routes/auth/callback.tsx den Exchange explizit.
      // Race: einer verbraucht Code + loescht code_verifier; der zweite
      // findet ihn nicht → "PKCE code verifier not found in storage" → Login-Loop.
      // Bei 100 Live-Kunden mit Google-OAuth reproduzierbar.
      // Hardcode-Regel: feedback_pkce_disable_detect_session_in_url.
      detectSessionInUrl: false,
      flowType: 'pkce',
      autoRefreshToken: true,
      persistSession: true,
      // DEV-ONLY (05.06.2026): No-op-Lock auf localhost. supabase-js nutzt im
      // Browser standardmäßig `navigatorLock` (navigator.locks); setSession()
      // UND refreshSession() greifen denselben Lock. Beim Dev-Auto-Login-Seed
      // (lib/dev-auto-login.ts) führte das zu einem Deadlock — setSession() hing
      // ewig auf dem Lock, während ein direkter /auth/v1/user-Fetch in 234ms
      // durchging. Auf localhost (Single-Tab-Dev) ist die Cross-Tab-Serialisierung
      // verzichtbar. In Prod greift der echte navigatorLock (Block tree-shaked).
      ...(isLocalhostDev()
        ? {
            lock: async <R>(
              _name: string,
              _acquireTimeout: number,
              fn: () => Promise<R>,
            ): Promise<R> => fn(),
          }
        : {}),
    },
  })
  // DEMO-ONLY (spdemo Phase 3, Codex-Review-HIGH 19.07.2026): supabase-js
  // signOut() default ist scope 'global' — auf der Demo-Instanz teilen sich
  // ALLE Besucher einen Supabase-User, ein globaler Revoke (Logout-Button,
  // login.tsx-Cleanup-Pfade) würde jedem anderen Besucher die Session killen.
  // Zentral hier statt an allen 10 signOut()-Call-Sites (2 Packages), damit
  // auch künftige Aufrufer abgedeckt sind. Inline-Env-Check statt
  // lib/demo-login.isDemoBuild(): der importiert selbst diese Factory
  // (Zirkelimport). In Prod-/Preview-Builds ist VITE_DEMO_MODE undefined —
  // Block inert, signOut unverändert global.
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const origSignOut = _client.auth.signOut.bind(_client.auth)
    _client.auth.signOut = (options) => origSignOut({ ...options, scope: 'local' })
  }
  return _client
}
